import React, { useState } from 'react';
import type { OutlineHeading, OutlineOverride } from '../../../lib/aiJobSummary';
import { Badge, Button, IconButton, SegmentedControl } from '../../ui';
import SectionHeading, { OpCard } from './SectionHeading';

const LEVELS = [1, 2, 3, 4] as const;
const LEVEL_OPTIONS = LEVELS.map((l) => ({ id: String(l), label: `H${l}` }));

/**
 * Content jobs: the article's headings, indented by level. The human can adjust
 * them; the adjusted version is saved on the spec summary (outline_override) and
 * `cockpit ai get` hands it to the build agent in place of the spec'd outline,
 * the same way a decision answer beats the recommendation.
 */
export default function ContentOutline({
  outline,
  override,
  readOnly,
  onSave,
}: {
  outline: OutlineHeading[];
  override?: OutlineOverride | null;
  readOnly: boolean;
  /** Saves the adjusted outline, or null to go back to the agent's. */
  onSave?: (headings: OutlineHeading[] | null, note: string | null) => Promise<void>;
}) {
  const adjusted = !!override?.headings?.length;
  const shown = adjusted ? override!.headings : outline;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<OutlineHeading[]>([]);
  const [note, setNote] = useState('');
  const [state, setState] = useState<'idle' | 'busy' | 'error'>('idle');
  const canEdit = !readOnly && !!onSave;

  const startEdit = () => {
    setDraft(shown.map((h) => ({ ...h })));
    setNote(override?.note || '');
    setState('idle');
    setEditing(true);
  };

  const save = async (headings: OutlineHeading[] | null, n: string | null) => {
    if (!onSave) return;
    setState('busy');
    try {
      await onSave(headings, n);
      setState('idle');
      setEditing(false);
    } catch {
      setState('error');
    }
  };

  const patch = (i: number, next: Partial<OutlineHeading>) => setDraft((rows) => rows.map((r, j) => (j === i ? { ...r, ...next } : r)));
  const move = (i: number, by: -1 | 1) =>
    setDraft((rows) => {
      const j = i + by;
      if (j < 0 || j >= rows.length) return rows;
      const copy = [...rows];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  const insertAfter = (i: number) =>
    setDraft((rows) => {
      const copy = [...rows];
      copy.splice(i + 1, 0, {
        level: rows[i]?.level === 1 ? 2 : (rows[i]?.level ?? 2),
        text: '',
      });
      return copy;
    });
  const remove = (i: number) => setDraft((rows) => rows.filter((_, j) => j !== i));

  const counts = shown.reduce<Record<number, number>>((acc, h) => ({ ...acc, [h.level]: (acc[h.level] || 0) + 1 }), {});

  return (
    <section>
      <SectionHeading
        eyebrow={adjusted ? 'Outline · your adjusted version, the build follows it' : 'Outline · as the agent spec’d it'}
        title="Headings"
        icon="list-checks"
        count={`${counts[2] || 0} H2 · ${counts[3] || 0} H3`}
        actions={
          editing ? null : (
            <>
              {adjusted ? (
                <Badge tone="accent" dot>
                  Adjusted
                </Badge>
              ) : null}
              {canEdit ? (
                <Button size="sm" variant="ghost" icon="pencil" onClick={startEdit}>
                  Adjust outline
                </Button>
              ) : null}
              {canEdit && adjusted ? (
                <Button
                  size="sm"
                  variant="ghost"
                  icon="rotate-ccw"
                  disabled={state === 'busy'}
                  title="Drop your version; the build follows the agent's outline"
                  onClick={() => save(null, null)}
                >
                  Use agent's
                </Button>
              ) : null}
            </>
          )
        }
      />

      {editing ? (
        <OpCard tone="raised" padding="10px 12px">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {draft.map((h, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  paddingLeft: `${(h.level - 1) * 18}px`,
                }}
              >
                <SegmentedControl
                  options={LEVEL_OPTIONS}
                  value={String(h.level)}
                  onChange={(id) => patch(i, { level: Number(id) as OutlineHeading['level'] })}
                  style={{ flexShrink: 0 }}
                />
                <input
                  className="ck-input"
                  value={h.text}
                  placeholder="Heading text"
                  onChange={(e) => patch(i, { text: e.target.value })}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    height: 'var(--control-sm)',
                    padding: '0 10px',
                    background: 'var(--surface-input)',
                    border: '1px solid var(--border-hairline)',
                    borderRadius: 'var(--radius-sm)',
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--text-body)',
                    outline: 'none',
                  }}
                />
                <IconButton
                  icon="chevron-down"
                  size="sm"
                  label="Move up"
                  title="Move up"
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                  style={{ transform: 'rotate(180deg)' }}
                />
                <IconButton
                  icon="chevron-down"
                  size="sm"
                  label="Move down"
                  title="Move down"
                  disabled={i === draft.length - 1}
                  onClick={() => move(i, 1)}
                />
                <IconButton icon="plus" size="sm" label="Add a heading below" title="Add a heading below" onClick={() => insertAfter(i)} />
                <IconButton icon="trash-2" size="sm" label="Remove" title="Remove" disabled={draft.length <= 1} onClick={() => remove(i)} />
              </div>
            ))}
          </div>
          <textarea
            className="ck-textarea"
            value={note}
            placeholder="Optional note to the writer (why the change, what to cover under a new heading)"
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            style={{ marginTop: '10px', width: '100%' }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '10px',
            }}
          >
            {state === 'error' ? (
              <span
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--status-down)',
                  marginRight: 'auto',
                }}
              >
                Couldn't save. Try again.
              </span>
            ) : null}
            <Button size="sm" variant="ghost" disabled={state === 'busy'} onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              icon="check"
              disabled={state === 'busy' || draft.every((h) => !h.text.trim())}
              onClick={() => save(draft, note.trim() || null)}
            >
              {state === 'busy' ? 'Saving…' : 'Save outline'}
            </Button>
          </div>
        </OpCard>
      ) : (
        <OpCard padding="10px 14px">
          <ol
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {shown.map((h, i) => (
              <li
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '10px',
                  padding: h.level <= 2 ? '8px 0 4px' : '3px 0',
                  paddingLeft: `${Math.max(0, h.level - 2) * 22}px`,
                  borderTop: h.level <= 2 && i > 0 ? '1px solid var(--border-hairline)' : 'none',
                }}
              >
                <span
                  className="t-num"
                  style={{
                    width: '22px',
                    flexShrink: 0,
                    fontSize: 'var(--text-xs)',
                    color: 'var(--text-faint)',
                  }}
                >
                  H{h.level}
                </span>
                <span style={{ minWidth: 0 }}>
                  <span
                    style={{
                      fontSize: h.level === 1 ? 'var(--text-md)' : 'var(--text-sm)',
                      fontWeight: (h.level <= 2 ? 'var(--weight-semibold)' : 'var(--weight-regular)') as React.CSSProperties['fontWeight'],
                      color: 'var(--text-body)',
                      lineHeight: 'var(--leading-snug)',
                    }}
                  >
                    {h.text}
                  </span>
                  {h.intent ? (
                    <span
                      style={{
                        display: 'block',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--text-muted)',
                        lineHeight: 'var(--leading-snug)',
                        marginTop: '2px',
                      }}
                    >
                      {h.intent}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ol>
          {adjusted && override?.note ? (
            <p
              style={{
                margin: '10px 0 2px',
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
                lineHeight: 'var(--leading-snug)',
              }}
            >
              Your note to the writer: {override.note}
            </p>
          ) : null}
        </OpCard>
      )}
    </section>
  );
}
